import { atom } from 'nanostores';
import { taskService, type Task } from '@/services/taskService';

// Константа для ключа localStorage
const PROGRESS_STORAGE_KEY = 'yc-containerland-progress';

// Типы для результата валидации
export interface ValidationError {
  path: string;
  expected: string;
  actual: any;
  message: string;
}

export interface ValidationResult {
  success: boolean;
  taskId: string;
  errors?: ValidationError[];
  message?: string;
  hint?: string;
}

// Типы для прогресса игрока
export interface PlayerProgress {
  currentStep: number;
  currentYaml: string | null;
  currentTask: Task | null;
  validationResult: ValidationResult | null;
  isLoading: boolean;
  error: string | null;
  errorCount: number; // Счетчик ошибок для текущего задания
  isGameCompleted: boolean; // Флаг завершенности игры
}

// Начальное состояние
const initialProgress: PlayerProgress = {
  currentStep: 1,
  currentYaml: null,
  currentTask: null,
  validationResult: null,
  isLoading: false,
  error: null,
  errorCount: 0,
  isGameCompleted: false,
};

// Основной стор прогресса
export const $progressStore = atom<PlayerProgress>(initialProgress);

// Функции для работы с localStorage
const saveProgressToStorage = (progress: PlayerProgress) => {
  try {
    const dataToSave = {
      currentStep: progress.currentStep,
      currentYaml: progress.currentYaml,
      isGameCompleted: progress.isGameCompleted,
    };
    localStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(dataToSave));
  } catch (error) {
    console.warn('Failed to save progress to localStorage:', error);
  }
};

const loadProgressFromStorage = (): Partial<PlayerProgress> | null => {
  try {
    const stored = localStorage.getItem(PROGRESS_STORAGE_KEY);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored);
    return {
      currentStep: parsed.currentStep || 1,
      currentYaml: parsed.currentYaml || null,
      isGameCompleted: parsed.isGameCompleted || false,
    };
  } catch (error) {
    console.warn('Failed to load progress from localStorage:', error);
    return null;
  }
};

// Действия для управления прогрессом
export const progressActions = {
  // Установить текущий шаг
  setCurrentStep: async (step: number) => {
    const newProgress = {
      ...$progressStore.get(),
      currentStep: step,
    };
    $progressStore.set(newProgress);
    saveProgressToStorage(newProgress);
    
    // Перезапрашиваем данные о задании для нового шага
    await progressActions.loadTaskByStep(step);
  },

  // Установить текущий YAML
  setCurrentYaml: (yaml: string | null) => {
    const newProgress = {
      ...$progressStore.get(),
      currentYaml: yaml,
    };
    $progressStore.set(newProgress);
    saveProgressToStorage(newProgress);
  },

  // Перейти к следующему шагу
  nextStep: () => {
    const current = $progressStore.get();
    const newProgress = {
      ...current,
      currentStep: current.currentStep + 1,
    };
    $progressStore.set(newProgress);
    saveProgressToStorage(newProgress);
  },

  // Перейти к предыдущему шагу
  prevStep: () => {
    const current = $progressStore.get();
    if (current.currentStep > 1) {
      const newProgress = {
        ...current,
        currentStep: current.currentStep - 1,
      };
      $progressStore.set(newProgress);
      saveProgressToStorage(newProgress);
    }
  },

  // Сбросить прогресс
  resetProgress: () => {
    $progressStore.set(initialProgress);
    try {
      localStorage.removeItem(PROGRESS_STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear progress from localStorage:', error);
    }
  },

  // Обновить весь прогресс
  updateProgress: (progress: Partial<PlayerProgress>) => {
    const newProgress = {
      ...$progressStore.get(),
      ...progress,
    };
    $progressStore.set(newProgress);
    if (progress.currentStep !== undefined || progress.currentYaml !== undefined) {
      saveProgressToStorage(newProgress);
    }
  },

  // Загрузить задачу по шагу
  loadTaskByStep: async (step: number) => {
    $progressStore.set({
      ...$progressStore.get(),
      isLoading: true,
      error: null,
    });

    const response = await taskService.getTaskByStep(step);

    if (response.success && response.task) {
      const newProgress = {
        ...$progressStore.get(),
        currentTask: response.task,
        currentStep: step,
        currentYaml: response.task.brokenManifest || null, // Устанавливаем brokenManifest в currentYaml
        errorCount: 0, // Сбрасываем счетчик ошибок при загрузке новой задачи
        isLoading: false,
      };
      $progressStore.set(newProgress);
      saveProgressToStorage(newProgress);
    } else {
      $progressStore.set({
        ...$progressStore.get(),
        error: response.error || 'Failed to load task',
        isLoading: false,
      });
    }
  },

  // Загрузить текущую задачу
  loadCurrentTask: async () => {
    const currentStep = $progressStore.get().currentStep;
    await progressActions.loadTaskByStep(currentStep);
  },

  // Перейти к следующему шагу с загрузкой задачи
  nextStepWithTask: async () => {
    const current = $progressStore.get();
    const nextStep = current.currentStep + 1;
    
    // Если завершили 5-е задание, отмечаем игру как завершенную
    if (current.currentStep === 5) {
      const newProgress = {
        ...current,
        currentStep: nextStep,
        isGameCompleted: true,
      };
      $progressStore.set(newProgress);
      saveProgressToStorage(newProgress);
      
      // Переходим на экран завершения вместо загрузки новой задачи
      const { setCurrentSection } = await import('@/stores/navigationStore');
      setCurrentSection('end');
      return;
    }
    
    // Для остальных шагов загружаем задачу как обычно
    await progressActions.loadTaskByStep(nextStep);
  },

  // Отправить решение
  submitSolution: async (yaml: string) => {
    const current = $progressStore.get();
    
    if (!current.currentTask) {
      $progressStore.set({
        ...current,
        error: 'No current task to submit solution for',
      });
      return false;
    }
    
    $progressStore.set({
      ...current,
      isLoading: true,
      error: null,
    });

    const response = await taskService.submitTaskSolution(current.currentTask.id, yaml);

    // Увеличиваем счетчик ошибок если валидация не прошла
    const newErrorCount = response.success ? 0 : current.errorCount + 1;

    // Обновляем состояние с результатом валидации
    $progressStore.set({
      ...$progressStore.get(),
      currentYaml: yaml,
      validationResult: response.validationResult || null,
      errorCount: newErrorCount,
      isLoading: false,
      error: response.success ? null : (response.error || 'Failed to submit solution'),
    });

    return response.success;
  },

  // Очистить результат валидации
  clearValidationResult: () => {
    $progressStore.set({
      ...$progressStore.get(),
      validationResult: null,
    });
  },

  // Инициализировать прогресс из localStorage
  initializeProgress: () => {
    const storedProgress = loadProgressFromStorage();
    if (storedProgress) {
      $progressStore.set({
        ...$progressStore.get(),
        ...storedProgress,
      });
      return storedProgress.currentStep || 1;
    }
    return 1;
  },
};

// Функция для инициализации состояния приложения с автоматической навигацией
export const initializeAppState = async () => {
  const currentStep = progressActions.initializeProgress();
  const currentState = $progressStore.get();
  
  // Добавляем отладочную информацию
  console.log('initializeAppState:', { currentStep, isGameCompleted: currentState.isGameCompleted });
  
  // Если игра завершена (6 шаг + флаг), отправляем на экран end
  if (currentState.isGameCompleted && currentStep === 6) {
    console.log('Redirecting to end screen');
    const { setCurrentSection } = await import('@/stores/navigationStore');
    setCurrentSection('end');
    return currentStep;
  }
  
  // Если шаг больше 1, но игра не завершена, отправляем на briefing
  if (currentStep > 1) {
    console.log('Redirecting to briefing');
    // Динамический импорт для избежания циклических зависимостей
    const { setCurrentSection } = await import('@/stores/navigationStore');
    setCurrentSection('briefing');
    
    // Загружаем задачу для текущего шага
    await progressActions.loadTaskByStep(currentStep);
  }
  
  return currentStep;
};